import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { AuthenticationService } from '@app/authentication/authentication.service';
import { successOnly } from '@shared/operators/success.operator';
import { ToastService } from '@shared/services/toast.service';

@Injectable({ providedIn: 'root' })
export class HttpHandlerService {

  constructor(
    public http: HttpClient,
    private authenticationService: AuthenticationService,
    private toastService: ToastService
  ) {}

  public encode(params: any): string {
    return Object.keys(params).map((key) => {
      return [key, params[key]].map(encodeURIComponent).join('=');
    }).join('&');
  }

  /**
   * @deprecated
   */
  public get<T>(url: string | any[], errorMessage: string, loadingMessage?: string, headers: any = this._headers): Observable<T> {
    url = this._urlHandle(url);
    let obs$ = this.http.get(url, headers);
    obs$ = this._loadingHandle(obs$, loadingMessage, errorMessage);
    return this._errorHandle(obs$, errorMessage);
  }

  /**
   * @deprecated
   */
  public post<T>(url: string | any[], body: any, errorMessage: string, loadingMessage?: string, headers: any = this._headers): Observable<T> {
    url = this._urlHandle(url);
    let obs$ = this.http.post(url, body, headers);
    obs$ = this._loadingHandle(obs$, loadingMessage, errorMessage);
    return this._errorHandle(obs$, errorMessage);
  }

  /**
   * @deprecated
   */
  public put<T>(url: string | any[], body: any, errorMessage: string, loadingMessage?: string, headers: any = this._headers): Observable<T> {
    url = this._urlHandle(url);
    let obs$ = this.http.put(url, body, headers);
    obs$ = this._loadingHandle(obs$, loadingMessage, errorMessage);
    return this._errorHandle(obs$, errorMessage);
  }

  /**
   * @deprecated
   */
  public patch<T>(url: string | any[], body: any, errorMessage: string, loadingMessage?: string, headers: any = this._headers): Observable<T> {
    url = this._urlHandle(url);
    let obs$ = this.http.patch(url, body, headers);
    obs$ = this._loadingHandle(obs$, loadingMessage, errorMessage);
    return this._errorHandle(obs$, errorMessage);
  }

  /**
   * @deprecated
   */
  public delete<T>(url: string | any[], errorMessage: string, loadingMessage?: string, headers: any = this._headers): Observable<T> {
    url = this._urlHandle(url);
    let obs$ = this.http.delete(url, headers);
    obs$ = this._loadingHandle(obs$, loadingMessage, errorMessage);
    return this._errorHandle(obs$, errorMessage);
  }

  private _errorHandle(observable$: Observable<any>, errorMessage: string): Observable<any> {
    return observable$.pipe(catchError(err => {
      if (errorMessage && errorMessage.length) { this.toastService.show(errorMessage, 'danger'); }
      console.error(err);
      return throwError(err);
    }));
  }

  private _urlHandle(url: string | any[]): string {
    if (Array.isArray(url) && typeof url[0] === 'string' && typeof url[1] === 'object') {
      return `${url[0]}?${this.encode(url[1])}`;
    } else if (Array.isArray(url)) {
      throw new Error(`O Array-URL passado não está no formato certo. Esperado: [baseUrl: string, params: Object]`);
    } else {
      return url;
    }
  }

  private _loadingHandle(observable: Observable<any>, message: string, errorMessage: string) {
    if (message && errorMessage) {
      this.toastService.showSnack(message);
      observable = observable.pipe(
        successOnly(() => this.toastService.hideSnack())
      );
    }
    return observable;
  }

  public request<T>(url: string) {
    return new RequestBuilder<T>(this.http, this._headers, url, this.toastService);
  }

  public filterMaker<T>(reference: T & object, ...keys: (keyof T)[]) {
    const filter: any = {};
    for (const key of keys) {
      filter[key] = reference[key];
    }
    return filter;
  }

  private get _headers() {
    const headers = this.authenticationService.getAuthorizationHeaders();
    return { headers };
  }

}


class RequestBuilder<T> {

  private body: any = {};

  private loadingMessage: string;
  private errorMessage: string;
  private successMessage: string;

  private errorMessageTime = 3000;
  private successMessageTime = 3000;

  constructor(
    private http: HttpClient,
    private headers: Object,
    private url: string,
    private toast: ToastService,
  ) {}

  public setBody(body: any) {
    this.body = body;
    return this;
  }

  public setHeaders(headers: any) {
    this.headers = headers;
    return this;
  }

  public setParams(params: any) {
    this.url += '?' + Object.keys(params).map((key) => {
      return [key, params[key]].map(encodeURIComponent).join('=');
    }).join('&');
    return this;
  }

  public setLoadingMessage(loadingMessage: string) {
    this.loadingMessage = loadingMessage;
    return this;
  }

  public setErrorMessage(errorMessage: string, ms = 3000) {
    this.errorMessage = errorMessage;
    this.errorMessageTime = ms;
    return this;
  }

  public setSuccesMessage(successMessage: string, ms = 3000) {
    this.successMessage = successMessage;
    this.successMessageTime = ms;
    return this;
  }

  public get() {
    const request$ = this.http.get<T>(this.url, this.headers);
    return this.handleObservable(request$);
  }

  public post() {
    const request$ = this.http.post<T>(this.url, this.body, this.headers);
    return this.handleObservable(request$);
  }

  public put() {
    const request$ = this.http.put<T>(this.url, this.body, this.headers);
    return this.handleObservable(request$);
  }

  public patch() {
    const request$ = this.http.patch<T>(this.url, this.body, this.headers);
    return this.handleObservable(request$);
  }

  public delete() {
    const request$ = this.http.delete<T>(this.url, this.headers);
    return this.handleObservable(request$);
  }

  private handleObservable(observable: Observable<T>) {
    if (this.loadingMessage) {
      this.toast.showSnack(this.loadingMessage);
    }

    return observable.pipe(
      catchError(err => {
        if (this.errorMessage) {
          this.toast.show(this.errorMessage, 'danger', this.errorMessageTime);
        }
        return throwError(err);
      }),
      map(result => {
        if (this.successMessage) {
          this.toast.show(this.successMessage, 'success', this.successMessageTime);
        } else {
          this.toast.hideSnack();
        }
        return result;
      })
    )
  }


}
